import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  Validators,
} from '@angular/forms';
import { GoogleRecapchaComponent } from '@components/auth/google-recapcha/google-recapcha.component';
import { AuthenService } from '@core/authen/authen.service';
import {
  FormComponentInterface,
  FormErrorService,
} from '@core/form/form-error.service';
import { SessionService } from '@core/session.service';
import { environment } from '@env/environment';
import { TranslateModule } from '@ngx-translate/core';
import { DebounceClickDirective } from '@shared/directives/debounce-click.directive';
import { LanguagePathDirective } from '@shared/directives/language-path.directive';
import { SessionKeyEnum } from '@shared/enums/session.key.enum';
import { ApiResponse } from '@shared/interfaces/responses/ApiResponse.interface';
import { EmailWrapperPipe } from '@shared/pipes/link-wrapper.pipe';
import { StringReplacementPipe } from '@shared/pipes/string-replacement.pipe';
import { TextValidator } from '@shared/utils/form-fields-validation';
import { FormUtils } from '@shared/utils/form-utils';
import { CookieService } from 'ngx-cookie-service';
@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  imports: [
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    GoogleRecapchaComponent,
    DebounceClickDirective,
    LanguagePathDirective,
    StringReplacementPipe,
    EmailWrapperPipe,
  ],
})
export class ForgotPasswordComponent implements OnInit, FormComponentInterface {
  @Output() sendConfirmationEmail = new EventEmitter<unknown>();

  portalName: string = environment.portalName;
  portalUrl: string = environment.portalUrl;

  confirmationForm: FormGroup;
  formErrorKey: string;
  errorMessage: Record<string, string>;
  isSubmitting?: boolean;
  isSubmitted?: boolean;

  public isSentEmail = false;
  public sentEmailAddress: string;
  public resetPasswordToken: string;
  sendEmailFalseMessage: string;

  constructor(
    private readonly fb: UntypedFormBuilder,
    readonly authenService: AuthenService,
    private readonly formErrorService: FormErrorService,
    private readonly sessionService: SessionService,
    private readonly cookieService: CookieService
  ) {
    this.portalName = this.cookieService.get(SessionKeyEnum.TENANT_NAME);
    this.portalUrl = this.cookieService.get(SessionKeyEnum.TENANT_DOMAIN);
  }

  getForm(): FormGroup {
    return this.confirmationForm;
  }

  ngOnInit(): void {
    if (this.sessionService.authenticated) {
      this.goTo(environment.jipUrl.profile);
    }

    this.initConfirmationForm();
  }

  goTo(url: string): void {
    FormUtils.navigateTo(this.sessionService.appendLanguagePath(url));
  }

  initConfirmationForm(): void {
    this.confirmationForm = this.fb.group({
      usernameOrEmail: [
        '',
        {
          validators: [
            Validators.required,
            TextValidator.notEmptyOrWhitespace,
            TextValidator.cannotContainWhitespace,
          ],
        },
      ],
      recaptcha: ['', Validators.required],
    });
    this.formErrorService.register(this);
  }

  get usernameOrEmail(): AbstractControl<string> {
    return this.confirmationForm.get('usernameOrEmail');
  }

  onSubmit(): void {
    this.isSubmitting = true;
    const usernameOrEmail = this.usernameOrEmail.value;
    this.sendResetPassword(usernameOrEmail);
  }

  sendResetPassword(usernameOrEmail: string): void {
    const sentToEmail = usernameOrEmail?.trim();
    this.authenService.requestResetPassword(sentToEmail).subscribe({
      next: (data: unknown) => {
        const error = data as ApiResponse;

        if (error?.status === 400) {
          this.formErrorService.handleError(data as HttpErrorResponse);
          this.isSubmitted = false;
        } else {
          this.isSentEmail = data['isEmailDelivered'];
          this.sendEmailFalseMessage = data['isEmailDelivered']
            ? ''
            : data['message'];
          this.sentEmailAddress = data['email'];
          this.resetPasswordToken = data['token'];
          this.sendConfirmationEmail.emit(data);
          this.isSubmitted = true;
        }
        this.isSubmitting = false;
      },
      error: error => {
        this.handleError(error);
      },
    });
  }

  resendResetPassword(usernameOrEmail: string, token: string): void {
    this.isSubmitting = true;
    this.isSubmitted = false;

    const sentToEmail = usernameOrEmail?.trim();
    this.authenService
      .resendRequestResetPassword(sentToEmail, token)
      .subscribe({
        next: (data: unknown) => {
          const error = data as ApiResponse;

          if (error?.status === 400) {
            this.formErrorService.handleError(data as HttpErrorResponse);
            this.isSubmitted = false;
          } else {
            this.isSentEmail = data['isEmailDelivered'];
            this.sentEmailAddress = sentToEmail;
            this.resetPasswordToken = data['token'];
            this.sendConfirmationEmail.emit(data);
            this.isSubmitted = true;
          }

          this.isSubmitting = false;
        },
        error: error => {
          this.handleError(error);
        },
      });
  }

  private handleError(error: HttpErrorResponse): void {
    this.isSubmitted = false;
    this.isSubmitting = false;
    this.formErrorService.handleError(error);
  }
}
